import type { ReactNode } from 'react';
import {
  EnvelopeSimpleIcon,
  PencilSimpleIcon,
  PhoneIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { OrderDetail } from '@skupreme/api-contracts/core';
import { useOrdersV2 } from '@features/orders/hooks/orders-v2';
import { getFlagSrc } from '@features/orders';
import { Badge, Button, Card, Field, Tooltip } from '@skupreme/shared/ui';
import {
  HideInvalidAddressOrderStatuses,
  tooltipTexts,
} from '@utility/constants/common';

export type CustomerCardProps = {
  order?: OrderDetail;
  /** Opens the address validation popup. */
  onEditAddress?: () => void;
  allowEdit?: boolean;
  isValidAddress?: boolean;
  addressValidationLoading?: boolean;
  addressValidationError?: string;
  addressValidationData?: { error?: string; description?: string };
  /** The plan's verdict on editing, from `useUrlPermission`. */
  isDisabled?: boolean;
  isHidden?: boolean;
  /** Set when editing is off for a reason other than the plan; shown as the button's tooltip. */
  readOnlyReason?: string;
  /** EN-4111: a control shown beside Edit (V2: "Address confirmed: ship anyway"). */
  /** Beside the address's problem (the Invalid address badge): what the operator can do about it. */
  addressAction?: ReactNode;
  /** EN-4111: the small line under the address once the ship-to was confirmed. */
  confirmedNote?: string | null;
  className?: string;
};

const hidesInvalidAddress = (status?: string) =>
  HideInvalidAddressOrderStatuses.includes(
    status as (typeof HideInvalidAddressOrderStatuses)[number],
  );

export function CustomerCard({
  order,
  onEditAddress,
  allowEdit,
  isValidAddress,
  addressValidationLoading,
  addressValidationError,
  addressValidationData,
  isDisabled,
  isHidden,
  readOnlyReason,
  addressAction,
  confirmedNote,
  className,
}: CustomerCardProps) {
  const { isV2 } = useOrdersV2();

  if (!order) {
    return (
      <Card title="Customer" className={className}>
        <div className="sk:h-24 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
      </Card>
    );
  }

  const customer = order.customer_details;
  const flag = getFlagSrc(customer?.country);
  const invalid = !isValidAddress && !addressValidationLoading;
  const cityAndState = [customer?.city, customer?.state]
    .filter(Boolean)
    .join(', ');
  const cityStateZip = [cityAndState, customer?.zip].filter(Boolean).join(' ');

  /* FBA keeps the address read-only once the order has left. */
  /* On V2 Edit follows the ship-to control alone: an invalid address on an order past editing has nothing to edit. */
  const canEdit =
    (allowEdit || (invalid && !isV2)) &&
    !isHidden &&
    !(order.is_fba && hidesInvalidAddress(order.summary?.status));

  const showInvalidBadge =
    !isValidAddress &&
    !hidesInvalidAddress(order.fulfillment_details?.fulfillment_status);

  const reason =
    addressValidationData?.error ||
    addressValidationData?.description ||
    addressValidationError;

  return (
    <Card
      title="Customer"
      action={
        canEdit ? (
          <Tooltip
            content={readOnlyReason || tooltipTexts.missingPermission}
            isDisabled={!isDisabled && !readOnlyReason}
            asChild
          >
            <Button
              variant="pill"
              size="sm"
              isDisabled={isDisabled || !!readOnlyReason}
              onPress={onEditAddress}
            >
              <PencilSimpleIcon size={12} />
              Edit
            </Button>
          </Tooltip>
        ) : null
      }
      className={className}
      bodyClassName="sk:flex sk:flex-col sk:gap-4"
    >
      {showInvalidBadge || addressAction ? (
        <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-2">
          {showInvalidBadge && (
            <Tooltip content={reason} isDisabled={!reason}>
              <Badge tone="danger">
                <WarningIcon size={12} weight="bold" />
                Invalid address
              </Badge>
            </Tooltip>
          )}
          {addressAction}
        </div>
      ) : null}

      <div className="sk:flex sk:flex-col sk:gap-0.5">
        <span className="sk:font-semibold">{customer?.name || '—'}</span>
        <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5 sk:text-fg-secondary">
          <EnvelopeSimpleIcon size={14} className="sk:shrink-0" />
          <span className="sk:truncate">{customer?.e_mail || '—'}</span>
        </span>
        <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5 sk:text-fg-secondary">
          <PhoneIcon size={14} className="sk:shrink-0" />
          <span className="sk:truncate">{customer?.phone || '—'}</span>
        </span>
      </div>

      <div className="sk:border-t sk:border-rule" />

      <section className="sk:flex sk:flex-col sk:gap-3">
        <h3 className="sk:m-0 sk:text-sm sk:font-semibold sk:text-fg">
          Shipping Address
        </h3>
        <div className="sk:flex sk:flex-col sk:gap-1">
          <span className="sk:min-w-0 sk:text-dense sk:font-medium sk:text-fg-secondary sk:tracking-dense">
            {[customer?.address_line_one, customer?.address_line_two]
              .filter(Boolean)
              .join(', ') || '—'}
          </span>
          <span className="sk:min-w-0 sk:text-dense sk:font-medium sk:text-fg-secondary sk:tracking-dense">
            {cityStateZip || '—'}
          </span>
          <span className="sk:min-w-0 sk:text-dense sk:font-medium sk:text-fg-secondary sk:tracking-dense">
            <span className="sk:inline-flex sk:items-center sk:gap-1.5">
              {flag ? (
                <img
                  src={flag}
                  alt=""
                  className="sk:h-3 sk:w-4 sk:shrink-0 sk:rounded-xs sk:object-cover"
                />
              ) : null}
              {customer?.country || '—'}
            </span>
          </span>
        </div>
        {confirmedNote ? (
          <span className="sk:text-xs sk:text-fg-secondary">
            {confirmedNote}
          </span>
        ) : null}
      </section>

      {customer?.note ? (
        <Field label="Customer note">
          <span className="sk:font-normal sk:text-fg-secondary">
            {customer.note}
          </span>
        </Field>
      ) : null}
    </Card>
  );
}
